import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import { trackerKeys } from './hooks';
import { aiAuthorOf } from '../profiles/timelineItems';
import type { SupabaseClient } from '../../lib/supabase';
import type { Profile } from '../auth/access';
import type { Database, Enums, Tables } from '../../types/database.types';

/**
 * The lead timeline: every email, call, message, meeting, note and reply.
 *
 * Kyu: "Add a universal 'Log email sent' action on every lead. It must work
 * even when the lead is not in a list."
 *
 * Before this, the only place the Hub could record an email was a list
 * membership, as one of four checkboxes. Most inbound leads are on no list, so
 * for them there was nowhere to put the fact that we replied — which is the
 * reason "Awaiting first touch" could not be trusted, and the reason it cannot
 * stay trustworthy without somewhere to write.
 *
 * ── Nothing here writes directly ───────────────────────────────────────────
 *
 * `lead_activity` has no insert, update or delete policy and no write grant.
 * Every write goes through `log_lead_activity`, because three things have to
 * happen together or not at all: the row lands, the counters move, and a New
 * lead becomes Contacted. Split across the client those are three round trips
 * with two chances to half-succeed.
 *
 * The counters — `leads.last_touch_at` and `leads.touch_count` — are written
 * by a database trigger and read here. Nothing in the app sets them.
 */

export type LeadActivity = Tables<'lead_activity'>;
export type LeadActivityKind = Enums<'lead_activity_kind'>;

/**
 * The WHOLE row, and it has to stay the whole row.
 *
 * `LeadActivity` is `Tables<'lead_activity'>` — the generated type for every
 * column — so a list short of one column is a lie the compiler believes until
 * somebody regenerates the types. That is exactly what happened: `copy_set_id`
 * arrived in 20261212000100 and `round_id` in 20261213000100, and neither
 * reached this list, because nothing re-ran the type generator in between.
 *
 * Two ways to keep it honest: fetch every column, or narrow the type to the
 * columns fetched. This takes the first, because the two new ids are uuids on
 * a per-lead query and the second would fan a narrower type out through
 * activityStats and the timeline for no gain.
 *
 * It happened a third time on 1 Sep: `external_id` arrived with the Gmail
 * sync work and this list did not know about it, because the generated types
 * were stale and so the compiler could not tell anyone. Regenerating them from
 * the migrations is what surfaced it. The lesson the paragraph above already
 * drew, drawn again: this list is only honest while the generated types are.
 */
const ACTIVITY_COLUMNS =
  'id, lead_id, prospect_id, kind, body, occurred_at, occurred_is_day, source, list_id, copy_set_id, message_slot, message_selected_at, message_version, round_id, touch_number, actor_id, external_id, created_at';

function fail(message: string): never {
  throw new Error(message);
}

/**
 * The kinds a person can log, in the order the buttons appear.
 *
 * `reply_received` is here because a reply is the most important thing that
 * happens to a lead and somebody has to be able to record one arriving by
 * phone or forwarded mail. It deliberately does NOT count as a touch: a touch
 * is outreach we sent, and counting a reply would make "4 touches, no reply"
 * a sentence that cannot be true.
 */
export const ACTIVITY_KINDS: { kind: LeadActivityKind; label: string; isTouch: boolean }[] = [
  { kind: 'email_sent', label: 'Email sent', isTouch: true },
  { kind: 'call', label: 'Call', isTouch: true },
  // Kyu, 22 September: Alex texts inbound ad leads before anyone emails or
  // calls, and those texts are outreach. Until 20270163000100 they could only
  // be logged as Notes, which count for nothing. A text is a touch, exactly
  // like a call: touch count, New to Contacted, and the round.
  { kind: 'text', label: 'Text', isTouch: true },
  { kind: 'linkedin', label: 'LinkedIn message', isTouch: true },
  // Sent and accepted are two kinds, not one: different days, different news,
  // and dealChecklist reads `linkedin_added` as evidence the contact is
  // actually reachable. Neither is a touch — until they are messaged nothing
  // has been said to them, and touch feeds the round arithmetic for every
  // lead (20270121000100).
  //
  // Hanneza, 14 September: a connection request is often the only thing that
  // CAN be done, because Senku's contacts arrive with no email address. It
  // counts as work the moment it is sent, and deliberately starts no
  // follow-up clock, because the only next step is waiting (20270144000100).
  { kind: 'linkedin_request', label: 'Connection request sent', isTouch: false },
  { kind: 'linkedin_added', label: 'Added on LinkedIn', isTouch: false },
  { kind: 'meeting', label: 'Meeting', isTouch: true },
  { kind: 'reply_received', label: 'Reply received', isTouch: false },
  { kind: 'note', label: 'Note', isTouch: false },
];

const KIND_LABELS = new Map(ACTIVITY_KINDS.map((entry) => [entry.kind, entry.label]));

export function activityLabel(kind: LeadActivityKind): string {
  return KIND_LABELS.get(kind) ?? kind;
}

/**
 * Whose name goes on a timeline entry.
 *
 * Hanneza, 3 Sep, on the first sync-logged rows: "oooh it's logging it but
 * it's unknown". A row the mail sync wrote has no actor — the sales mailbox
 * is shared, so Gmail cannot say who typed it — and "Unknown" read as a fault.
 * It is not: say where the row came from instead. A colleague's email carries
 * their name (drop 84); a Hub send carries the sender's; only mail read out
 * of the shared mailbox is nameless, and now says so.
 */
export function actorLabel(
  activity: Pick<LeadActivity, 'actor_id' | 'source' | 'kind' | 'external_id'>,
  names: Map<string, string>,
): string {
  if (wasRecordedBeforeTheTimeline(activity)) {
    return 'Recorded before the timeline existed';
  }
  const ai = aiAuthorOf(activity.source);
  if (ai !== undefined) {
    return ai.name;
  }
  if (activity.actor_id !== null) {
    return names.get(activity.actor_id) ?? 'Someone at TAP';
  }
  if (activity.external_id !== null) {
    return activity.kind === 'reply_received'
      ? 'The lead · found in the sales mailbox'
      : 'Sales mailbox (alexjschulze@) · found by the sync';
  }
  return 'Unknown';
}

/** Mirrors `public.lead_activity_is_touch`. Kept in step by a test. */
export function isTouchKind(kind: LeadActivityKind): boolean {
  return ACTIVITY_KINDS.find((entry) => entry.kind === kind)?.isTouch === true;
}

export const activityKeys = {
  forLead: (leadId: string) => [...trackerKeys.all, 'activity', leadId] as const,
};

export async function fetchLeadActivity(
  client: SupabaseClient,
  leadId: string,
): Promise<LeadActivity[]> {
  const { data, error } = await client
    .from('lead_activity')
    .select(ACTIVITY_COLUMNS)
    .eq('lead_id', leadId)
    .order('occurred_at', { ascending: false });
  if (error) {
    fail(error.message);
  }
  return Array.isArray(data) ? data : [];
}

/**
 * The touches a set of leads has that no round owns yet.
 *
 * ── Why this exists separately from useLeadActivity ────────────────────────
 *
 * The bulk round panel has to say how much sending a round actually asks for,
 * and a lead who has already had four emails asks for three, not seven. The
 * drawer answers that from one lead's full timeline; the table cannot, because
 * pulling every activity row for a selection of a hundred leads to count a
 * handful of them is a page-load somebody waits through.
 *
 * So this asks a narrower question: for THESE leads, which unclaimed touches
 * are there and when. Replies are excluded — a reply does not raise the count,
 * and the panel is counting sends.
 *
 * Keyed on the sorted id list, so re-ticking the same leads is a cache hit and
 * changing the selection is a new question.
 */
export const unclaimedKeys = {
  forLeads: (leadIds: readonly string[]) =>
    [...trackerKeys.all, 'unclaimed-touches', [...leadIds].sort().join(',')] as const,
};

export async function fetchUnclaimedTouches(
  client: SupabaseClient,
  leadIds: readonly string[],
): Promise<Map<string, string[]>> {
  const byLead = new Map<string, string[]>();
  if (leadIds.length === 0) {
    return byLead;
  }
  const { data, error } = await client
    .from('lead_activity')
    .select('lead_id, kind, occurred_at')
    .in('lead_id', [...leadIds])
    .is('round_id', null)
    .order('occurred_at', { ascending: true });
  if (error) {
    fail(error.message);
  }
  for (const row of Array.isArray(data) ? data : []) {
    // lead_id is nullable since 20270121000100 (a row may belong to a
    // prospect instead). The query asks for these leads only, so null cannot
    // arrive; the guard keeps the compiler honest rather than the data.
    if (row.lead_id === null || !isTouchKind(row.kind)) {
      continue;
    }
    byLead.set(row.lead_id, [...(byLead.get(row.lead_id) ?? []), row.occurred_at]);
  }
  return byLead;
}

export function useUnclaimedTouches(leadIds: readonly string[]) {
  const { client } = useAuth();
  return useQuery({
    queryKey: unclaimedKeys.forLeads(leadIds),
    queryFn: () => fetchUnclaimedTouches(client, leadIds),
    enabled: leadIds.length > 0,
  });
}

/**
 * The same rows across several leads: everyone at a company. Read for the
 * company panel's Activity tab (drop 4 of design-note-profiles-interlinked).
 */
export async function fetchLeadActivityFor(
  client: SupabaseClient,
  leadIds: readonly string[],
): Promise<LeadActivity[]> {
  if (leadIds.length === 0) return [];
  const { data, error } = await client
    .from('lead_activity')
    .select(ACTIVITY_COLUMNS)
    .in('lead_id', [...leadIds])
    .order('occurred_at', { ascending: false })
    .limit(500);
  if (error) {
    fail(error.message);
  }
  return data;
}

export function useLeadActivityFor(leadIds: readonly string[]) {
  const { client } = useAuth();
  return useQuery({
    queryKey: [...trackerKeys.all, 'activity', 'many', [...leadIds].sort().join(',')],
    queryFn: () => fetchLeadActivityFor(client, leadIds),
    enabled: leadIds.length > 0,
  });
}

export function useLeadActivity(leadId: string) {
  const { client } = useAuth();
  return useQuery({
    queryKey: activityKeys.forLead(leadId),
    queryFn: () => fetchLeadActivity(client, leadId),
    enabled: leadId !== '',
  });
}

/**
 * Log one thing that happened.
 *
 * `occurredOn` is a plain `YYYY-MM-DD` day and it is optional. Omitted means
 * "now", and now is an instant with a real time of day. Given, it means a
 * calendar day — an email sent on Friday and logged on Monday must read as
 * Friday, or the follow-up clock starts three days late.
 *
 * ── Send the day as a day ──────────────────────────────────────────────────
 *
 * This used to convert the picked date to an instant in the browser and send
 * that, which caused both of the date defects found in production: the
 * database compared a midday-local instant against `now()` in UTC and refused
 * today's date every morning before local noon. The browser has no business
 * inventing a time of day nobody typed, so it no longer does — the day travels
 * as a day and `activity_day_instant` turns it into one instant, once, in one
 * place. See 20261124000100.
 *
 * Both the timeline and the leads list are invalidated: the counters live on
 * the lead row, so a log that did not refresh the list would leave the Last
 * touch column showing yesterday's answer.
 */
export function useLogLeadActivity() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      leadId: string;
      kind: LeadActivityKind;
      body?: string;
      /** A calendar day, `YYYY-MM-DD`. Omit for "now". */
      occurredOn?: string;
      /** The campaign this email belongs to. The lead must be on it. */
      listId?: string;
      /** The copy used. Allowed without a list — see 20261217000100. */
      copySetId?: string;
    }): Promise<string> => {
      const { data, error } = await client.rpc('log_lead_activity', {
        p_lead: input.leadId,
        p_kind: input.kind,
        p_body: input.body ?? '',
        // Omitted rather than passed as undefined: omitting it means now(),
        // and sending the key at all would override that with null.
        ...(input.occurredOn === undefined ? {} : { p_occurred_on: input.occurredOn }),
        ...(input.listId === undefined ? {} : { p_list: input.listId }),
        ...(input.copySetId === undefined ? {} : { p_copy_set: input.copySetId }),
      });
      if (error) {
        throw new Error(error.message);
      }
      return typeof data === 'string' ? data : '';
    },
    onSettled: (_data, _error, input) => {
      void queryClient.invalidateQueries({ queryKey: activityKeys.forLead(input.leadId) });
      void queryClient.invalidateQueries({ queryKey: trackerKeys.leads() });
    },
  });
}

/**
 * Naming the campaign behind an email that is already on the timeline.
 *
 * Kyu picked "log it, then ask" over putting two dropdowns on a one-click
 * button: the send records instantly, exactly as before, and the campaign is
 * an optional second click on the row just written. Nobody is slowed down, and
 * dismissing the prompt is a recorded answer rather than a silence.
 *
 * Passing neither id CLEARS the attribution, which is what "Not a campaign"
 * sends. That is deliberate — somebody who picked the wrong campaign has to be
 * able to take it back, because a wrong credit in the numbers is worse than no
 * credit at all.
 *
 * The database refuses a mirrored row and a list the lead is not on; this only
 * asks. Both refusals have their own sentence in `activityErrorMessage`.
 */
export function useAttributeLeadActivity() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      activityId: string;
      /** LinkedIn campaign and message are independent. CAS uses the actual
       * displayed row and this writer never clears its selected message. */
      linkedinActivity?: LeadActivity;
      /** Omitted or undefined clears it. */
      listId?: string;
      copySetId?: string;
      /** Only for cache invalidation — the database reads it off the row. */
      leadId: string;
    }): Promise<void> => {
      if (input.linkedinActivity !== undefined) {
        const row = input.linkedinActivity;
        type Args = Database['public']['Functions']['set_linkedin_activity_campaign']['Args'];
        const args = {
          p_activity: row.id,
          p_lead: input.leadId,
          p_list: input.listId ?? null,
          p_expected_list: row.list_id,
          p_expected_copy: row.copy_set_id,
          p_expected_slot: row.message_slot,
          p_expected_selected_at: row.message_selected_at,
          p_expected_version: row.message_version,
        } satisfies { [Key in keyof Args]: Args[Key] | null };
        // Required PostgreSQL arguments accept explicit NULL; generated Args
        // does not model their nullable CAS semantics.
        const { error } = await client.rpc('set_linkedin_activity_campaign', args as Args);
        if (error) throw new Error(error.message);
        return;
      }
      const { error } = await client.rpc('attribute_lead_activity', {
        p_activity: input.activityId,
        ...(input.listId === undefined ? {} : { p_list: input.listId }),
        ...(input.copySetId === undefined ? {} : { p_copy_set: input.copySetId }),
      });
      if (error) {
        throw new Error(error.message);
      }
    },
    onSettled: (_data, _error, input) => {
      void queryClient.invalidateQueries({ queryKey: activityKeys.forLead(input.leadId) });
    },
  });
}

/**
 * Whether the viewer may remove one mistaken entry.
 *
 * Kyu: "admin can remove one mistaken timeline entry, but admin-only and the
 * removal itself must be logged." The database enforces both halves in
 * `remove_lead_activity`; this mirrors the gate so the button only appears
 * where pressing it can work.
 *
 * Manual entries only: a list-mirrored row belongs to its T1–T4 checkbox —
 * unticking the box is its delete button — and removing it here would leave
 * the checkbox and the timeline disagreeing about the same email.
 */
export function canRemoveEntry(
  profile: Pick<Profile, 'internal_role' | 'is_owner'> | null,
  activity: Pick<LeadActivity, 'source'>,
): boolean {
  if (profile === null) {
    return false;
  }
  const isAdmin = profile.internal_role === 'tap_admin' || profile.is_owner === true;
  return isAdmin && activity.source === 'manual';
}

/**
 * Remove one mistaken entry, with the reason the audit line will carry.
 *
 * The database deletes the row, recounts the lead's touch counters, and
 * writes who removed what and why to sales_activity — append-only, so the
 * log of the removal cannot itself be removed. The lead's status never
 * moves.
 */
export function useRemoveLeadActivity() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { leadId: string; activityId: string; reason: string }) => {
      const { error } = await client.rpc('remove_lead_activity', {
        p_activity: input.activityId,
        p_reason: input.reason,
      });
      if (error) {
        throw new Error(error.message);
      }
    },
    onSettled: (_data, _error, input) => {
      void queryClient.invalidateQueries({ queryKey: activityKeys.forLead(input.leadId) });
      void queryClient.invalidateQueries({ queryKey: trackerKeys.leads() });
    },
  });
}

/**
 * Whether this entry was mirrored from a T1–T4 checkbox rather than logged by
 * a person.
 *
 * Worth showing, because those rows are owned by the checkbox: unticking it
 * removes them. Somebody who cannot find the delete button for one of these
 * should be looking at the list, not the timeline.
 */
export function isFromList(activity: LeadActivity): boolean {
  return activity.source === 'list_touch' || activity.source === 'backfill';
}

/**
 * Entries from before the timeline existed carry no actor.
 *
 * The Hub never recorded who ticked a T1–T4 box, so the backfill left
 * `actor_id` null rather than putting somebody's name on it. The UI says so
 * plainly instead of rendering a blank where a name should be.
 */
export function wasRecordedBeforeTheTimeline(
  activity: Pick<LeadActivity, 'source' | 'actor_id'>,
): boolean {
  return activity.source === 'backfill' && activity.actor_id === null;
}

/**
 * The day an activity happened, as text somebody can read.
 *
 * A day-valued row — a T1–T4 tick, or a backdated manual entry — is stored at
 * midday UTC and MUST be formatted in UTC. Formatted in the reader's timezone
 * it drifts to the day before west of Greenwich and the day after east of
 * UTC+11, which is the defect this whole migration exists to close.
 *
 * An instant — the one-click Log email sent — is a real moment and is
 * formatted where the reader is, like any other timestamp.
 *
 * `readerTimeZone` is an argument rather than ambient, and that is deliberate.
 * What this function does depends on where the reader is, so a test that
 * cannot say where the reader is cannot test it — which is exactly what
 * happened here: the first version of this test passed in a UTC container
 * with the UTC rule deleted. Production passes nothing and gets the browser's
 * own zone.
 */
export function activityDayLabel(
  activity: LeadActivity,
  options: { locale?: string; readerTimeZone?: string } = {},
): string {
  const date = new Date(activity.occurred_at);
  if (Number.isNaN(date.getTime())) {
    return activity.occurred_at;
  }
  const zone = activity.occurred_is_day ? 'UTC' : options.readerTimeZone;
  return date.toLocaleDateString(options.locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    ...(zone === undefined ? {} : { timeZone: zone }),
  });
}

/**
 * Whether this was logged well after it happened.
 *
 * A day's grace: logging Friday's email on Friday evening is not "recorded
 * late", and flagging it would make the badge meaningless. Two days later is.
 *
 * A day-valued row sits at midday UTC, so a same-day log can be up to twelve
 * hours either side of it. The 36-hour window absorbs that without letting a
 * genuinely late entry through unmarked.
 */
export function wasRecordedLate(activity: LeadActivity): boolean {
  const occurred = new Date(activity.occurred_at).getTime();
  const recorded = new Date(activity.created_at).getTime();
  if (Number.isNaN(occurred) || Number.isNaN(recorded)) {
    return false;
  }
  return recorded - occurred > 36 * 60 * 60 * 1000;
}

/**
 * Whole days since the last touch. null when nobody has touched the lead.
 *
 * ── Count calendar days, not elapsed hours ─────────────────────────────────
 *
 * "6 days ago" is a statement about dates on a calendar, not about a duration.
 * Somebody looking at a touch dated the 14th on the 21st expects to read
 * "7 days ago" whatever o'clock either of them happened at.
 *
 * Measuring elapsed milliseconds got this wrong the moment day-valued touches
 * moved to midday UTC: the gap from midday on the 14th to midnight on the 21st
 * is 6.5 days, which floors to 6. Yesterday's touch measured half a day and
 * read as "Today", and the 14-day follow-up view fired a day late — three
 * symptoms of one arithmetic that was never asking the right question.
 *
 * This is the same mistake as the one that produced the day-shift: mixing
 * instant arithmetic with a calendar. So both sides are reduced to a calendar
 * day first, and then subtracted.
 */
export function daysSinceTouch(lastTouchAt: string | null, nowIso: string): number | null {
  if (lastTouchAt === null) {
    return null;
  }
  const then = new Date(lastTouchAt);
  const now = new Date(nowIso);
  if (Number.isNaN(then.getTime()) || Number.isNaN(now.getTime())) {
    return null;
  }
  const thenDay = Date.UTC(then.getUTCFullYear(), then.getUTCMonth(), then.getUTCDate());
  const nowDay = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  // Clamped: a touch logged this evening can carry tomorrow's UTC date, and
  // "-1 days ago" in a column somebody scans for overdue work is worse than
  // the few hours of imprecision it hides.
  return Math.max(0, Math.round((nowDay - thenDay) / 86_400_000));
}

/** "Today", "Yesterday", "12 days ago", or "Never" — for the Last touch column. */
export function lastTouchLabel(lastTouchAt: string | null, nowIso: string): string {
  const days = daysSinceTouch(lastTouchAt, nowIso);
  if (days === null) {
    return 'Never';
  }
  if (days === 0) {
    return 'Today';
  }
  if (days === 1) {
    return 'Yesterday';
  }
  return `${String(days)} days ago`;
}

/** The database's refusals, in words somebody can act on. */
export function activityErrorMessage(raw: string): string {
  if (raw.includes('activity_in_the_future')) {
    return 'That date is in the future. Check the day and year.';
  }
  if (raw.includes('not_a_sales_member')) {
    return 'You do not have access to the Outbound Tracker, so this cannot be logged.';
  }
  if (raw.includes('lead_not_found')) {
    return 'That lead no longer exists. Refresh the page.';
  }
  if (raw.includes('timeline_removal_needs_admin')) {
    return 'Only a TAP admin can remove a timeline entry.';
  }
  if (raw.includes('removal_reason_required')) {
    return 'Give a reason — the removal is logged with it.';
  }
  if (raw.includes('only_manual_entries_removable')) {
    return 'This entry mirrors a T1–T4 checkbox. Untick the touch on the list instead.';
  }
  if (raw.includes('activity_not_found')) {
    return 'That entry is already gone. Refresh the page.';
  }
  if (raw.includes('lead_not_on_that_list')) {
    return 'This lead is not on that list, so the campaign cannot claim the email. Add them to the list first, or leave the campaign blank.';
  }
  if (raw.includes('activity_not_manual')) {
    return 'This entry mirrors a T1–T4 checkbox, so its campaign comes from the list. Change it on the list instead.';
  }
  return 'That could not be logged. Try again.';
}
